import type { JSX } from 'react'
import { useEffect } from 'react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import type { Toast } from '@/stores/toasts'
import { useToastStore } from '@/stores/toasts'
import { IconButton } from './Button'
import { Button } from './Button'

/**
 * Тосты (docs/10 §5): `aria-live="polite"`, фокус не крадут, автозакрытие
 * 3–5 с, у удаления — «Отменить» в течение 5 с.
 */
export function Toaster(): JSX.Element {
  const { t } = useTranslation()
  const toasts = useToastStore(state => state.toasts)

  return (
    <div
      aria-live="polite"
      aria-label={t('toast.region')}
      // left-0/right-0 + mx-auto вместо left-1/2 + translate: перевод по X
      // выносил контейнер за правый край и давал горизонтальный скролл на 375px
      className="pointer-events-none fixed inset-x-0 bottom-4 z-toast mx-auto flex max-w-100 flex-col gap-2 px-4 sm:right-4 sm:left-auto sm:mx-0"
    >
      {toasts.map(item => (
        <ToastRow key={item.id} toast={item} />
      ))}
    </div>
  )
}

const TONE_CLASS: Record<Toast['tone'], string> = {
  info: 'border-border',
  success: 'border-online/50',
  warning: 'border-degraded/50',
  danger: 'border-danger/60'
}

function ToastRow({ toast }: { toast: Toast }): JSX.Element {
  const { t } = useTranslation()
  const dismiss = useToastStore(state => state.dismiss)

  useEffect(() => {
    if (toast.timeoutMs <= 0) return
    const timer = globalThis.setTimeout(() => dismiss(toast.id), toast.timeoutMs)
    return () => globalThis.clearTimeout(timer)
  }, [toast.id, toast.timeoutMs, dismiss])

  return (
    <div
      className={cn(
        'pointer-events-auto flex items-center gap-3 rounded-md border bg-surface-2 px-4 py-3 shadow-2',
        TONE_CLASS[toast.tone]
      )}
    >
      <p className="flex-1 text-base text-fg">{toast.message}</p>
      {toast.undo === undefined ? null : (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            toast.undo?.run()
            dismiss(toast.id)
          }}
        >
          {toast.undo.label}
        </Button>
      )}
      <IconButton size="sm" aria-label={t('toast.close')} onClick={() => dismiss(toast.id)}>
        <X aria-hidden className="size-4" />
      </IconButton>
    </div>
  )
}
